import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
} from 'ag-grid-community';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { enableFilterOptionTabFocus } from '../../../columns/filtering/filter-option-tab-focus';
import {
  alertTitleComparator,
  titleComparator,
} from '../../../sorting/sort-utils';
import { ActionsRendererComponent } from '../actions-renderer/actions-renderer.component';
import { BccRendererComponent } from '../bcc-renderer/bcc-renderer.component';
import { CcRendererComponent } from '../cc-renderer/cc-renderer.component';
import { FrequencyRendererComponent } from '../frequency-renderer/frequency-renderer.component';
import { ProductsRendererComponent } from '../products-renderer/products-renderer.component';
import { StatusRendererComponent } from '../status-renderer/status-renderer.component';
import { SubjectRendererComponent } from '../subject-renderer/subject-renderer.component';
import { TitleRendererComponent } from '../title-renderer/title-renderer.component';

@Injectable({
  providedIn: 'root',
})
export class AlertGridOptionsService {
  private columnDefs: ColDef[];
  private gridApi?: GridApi;
  private highlightEventListeners: { type: string; fn: any }[] = [];
  private refreshSubject = new Subject<void>();

  constructor() {
    this.columnDefs = [
      {
        headerName: 'Alert Title',
        headerClass: 'alert-title-header',
        field: 'title',
        cellRenderer: TitleRendererComponent,
        pinned: 'left',
        lockPinned: true,
        lockPosition: 'left',
        sortable: true,
        sort: 'asc',
        sortingOrder: ['asc', 'desc'],
        valueGetter: (params) => ({
          title: params.data?.title || '',
          systemGenerated: params.data?.systemGenerated || false,
        }),
        keyCreator: (params: any) => params.data?.title || '',
        comparator: titleComparator,
        width: 325,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          refreshValuesOnOpen: true,
          comparator: alertTitleComparator,
          valueFormatter: (params: any) => params.value?.title || '',
          cellHeight: 50,
        },
      },
      {
        headerName: 'Status',
        field: 'enabled',
        cellRenderer: StatusRendererComponent,
        width: 150,
        minWidth: 100,
        sortable: false,
      },
      {
        headerName: 'Actions',
        field: 'actions',
        cellRenderer: ActionsRendererComponent,
        width: 180,
        sortable: false,
      },
      {
        headerName: 'Orders',
        field: 'orders',
        width: 120,
        sortable: false,
      },
      {
        headerName: 'Product(s)',
        field: 'products',
        cellRenderer: ProductsRendererComponent,
        width: 150,
        sortable: false,
        cellStyle: {
          'padding-top': '0',
          display: 'flex',
          'align-items': 'center',
        },
      },
      {
        headerName: 'Frequency',
        field: 'frequency',
        cellRenderer: FrequencyRendererComponent,
        width: 480,
        minWidth: 180,
        sortable: false,
      },
      {
        headerName: 'Subject',
        field: 'subject',
        cellRenderer: SubjectRendererComponent,
        width: 200,
        sortable: false,
      },
      {
        headerName: 'Cc',
        field: 'cc',
        cellRenderer: CcRendererComponent,
        autoHeight: true,
        width: 725,
        maxWidth: 725,
        sortable: false,
        cellStyle: {
          padding: '1rem',
          'text-align': 'center',
        },
      },
      {
        headerName: 'Bcc',
        field: 'bcc',
        cellRenderer: BccRendererComponent,
        autoHeight: true,
        width: 725,
        maxWidth: 725,
        sortable: false,
        cellStyle: {
          padding: '1rem',
          'text-align': 'center',
        },
      },
    ];
  }

  public get gridOptions(): GridOptions {
    return {
      theme: 'legacy',
      rowModelType: 'clientSide',
      columnMenu: 'legacy',
      columnDefs: this.columnDefs,
      defaultColDef: {
        resizable: true,
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        cellStyle: {
          'padding-top': '2.2rem',
        },
        suppressKeyboardEvent: () => {
          return true;
        },
      },
      rowHeight: 100,
      headerHeight: 35,
      rowSelection: 'multiple',
      pagination: false,
      suppressColumnVirtualisation: true,
      suppressRowHoverHighlight: true,
      suppressCellFocus: true,
      suppressRowClickSelection: true,
      suppressDragLeaveHidesColumns: true,
      isFullWidthRow: (params) => !!params?.rowNode?.data?._panelRow,
      fullWidthCellRenderer: 'panelViewRowRenderer',
      postSortRows: (params: any) => {
        const nodes = params?.nodes;
        if (!Array.isArray(nodes) || nodes.length === 0) {
          return;
        }

        const PANEL_SUFFIX = '__panel';
        const panelMap = new Map<string, any>();
        const normalRows: any[] = [];

        for (const n of nodes) {
          const d = n?.data;
          if (!d) {
            continue;
          }
          if (d._panelRow) {
            const key = String(d.id ?? '');
            const masterId = key.endsWith(PANEL_SUFFIX)
              ? key.slice(0, -PANEL_SUFFIX.length)
              : key;
            panelMap.set(masterId, n);
          } else {
            normalRows.push(n);
          }
        }

        const nonSystem: any[] = [];
        const system: any[] = [];

        for (const row of normalRows) {
          const id = String(row?.data?.id ?? '');
          const isSystem = !!row?.data?.systemGenerated;
          const target = isSystem ? system : nonSystem;

          target.push(row);

          const panel = panelMap.get(id);
          if (panel) {
            target.push(panel);
            panelMap.delete(id);
          }
        }

        for (const panel of panelMap.values()) {
          nonSystem.push(panel);
        }

        nodes.splice(0, nodes.length, ...nonSystem, ...system);
      },
      getRowId: (params: GetRowIdParams) => params.data.id,
      getRowClass: (params) =>
        params.data._highlighted ? 'highlighted-row' : '',
      onFilterOpened: (event) => enableFilterOptionTabFocus(event),
    };
  }

  setGridApi(api: GridApi): void {
    this.gridApi = api;
    (this.gridApi as any)?.addEventListener?.call(
      this.gridApi,
      'sortChanged',
      () => this.gridApi?.ensureIndexVisible(0, 'top'),
    );
  }

  public getGridApi(): GridApi | undefined {
    return this.gridApi;
  }

  public refreshGrid(): void {
    this.refreshSubject.next();
  }

  public getRefreshObservable(): Observable<void> {
    return this.refreshSubject.asObservable();
  }

  public refreshData(value: any, field: any, rowId: string): void {
    const node = this.gridApi?.getRowNode(rowId);
    if (node) {
      node.data[field] = value;
      this.gridApi?.refreshCells({ rowNodes: [node], force: true });
    }
  }

  addHighlightedRow(newRowData: any): void {
    if (!this.gridApi) {
      return;
    }
    newRowData._highlighted = true;

    const result = this.gridApi.applyTransaction({
      add: [newRowData],
    });

    if (result?.add?.length) {
      const node = result.add[0];
      if (node.rowIndex !== null) {
        this.gridApi.ensureIndexVisible(node.rowIndex, 'middle');
      }
    }
    setTimeout(() => {
      const rowNode = this.gridApi?.getRowNode(newRowData.id);
      if (rowNode) {
        this.gridApi?.ensureNodeVisible(rowNode, 'middle');
      }
    }, 100);

    const removeHighlight = () => {
      setTimeout(() => {
        this.gridApi?.forEachNode((node: any) => {
          if (node?.data?._highlighted) {
            node.data._highlighted = false;
            this.gridApi?.redrawRows({ rowNodes: [node] });
          }
        });
        this.removeHighlightEventListeners();
      });
    };

    this.removeHighlightEventListeners();

    this.highlightEventListeners = [
      { type: 'sortChanged', fn: removeHighlight },
    ];

    this.highlightEventListeners.forEach((listener) =>
      this.gridApi?.addEventListener(listener.type as any, listener.fn),
    );
  }

  private removeHighlightEventListeners(): void {
    if (this.highlightEventListeners?.length) {
      this.highlightEventListeners.forEach((listener) =>
        this.gridApi?.removeEventListener(listener.type as any, listener.fn),
      );
      this.highlightEventListeners = [];
    }
  }

  public getFilteredWatchAlerts(
    parentGridApi: any,
    currentAlertId: string,
  ): { id: string; title: string }[] {
    const watchAlerts: { id: string; title: string }[] = [];

    if (parentGridApi) {
      parentGridApi.forEachNodeAfterFilterAndSort((node: any) => {
        const data = node.data;
        if (data) {
          const id = data.id;
          const title = data.title;
          const isSystemGenerated = data.systemGenerated ?? false;
          const isPanelRow = data._panelRow ?? false;

          if (String(id) === String(currentAlertId)) {
            return;
          }

          if (id && title && !isSystemGenerated && !isPanelRow) {
            watchAlerts.push({
              id: String(id),
              title: String(title),
            });
          }
        }
      });
    }

    return watchAlerts.sort((a, b) => a.title.localeCompare(b.title));
  }

  public clearAlertFilters(): void {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
    }
  }
}
